import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const fmt = v => new Intl.NumberFormat('es-CO',
  { style:'currency', currency:'COP', minimumFractionDigits:0 }).format(v || 0);
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

/**
 * El cuadre del mes: qué se le debe a cada vendedor y qué ya se le pagó.
 *
 * Lo que hace útil esto es que al liquidar las cifras quedan congeladas. El reporte normal
 * calcula al vuelo, así que si mañana se anula una factura de marzo, marzo cambia solo. Con
 * el cuadre, lo pagado queda como está —la plata ya salió— y el sistema avisa que la cuenta
 * de hoy ya no coincide, para poder decidir qué hacer.
 */
export default function CuadreMensual({ esAdmin }) {
  const ahora = new Date();
  const [anio, setAnio] = useState(ahora.getFullYear());
  const [mes, setMes] = useState(ahora.getMonth() + 1);
  const [datos, setDatos] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [msg, setMsg] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        api.get(`/api/vendedores/cuadre?anio=${anio}&mes=${mes}`),
        api.get('/api/vendedores/liquidaciones'),
      ]);
      setDatos(c.data);
      setHistorial(h.data || []);
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  }, [anio, mes]);

  useEffect(() => { cargar(); }, [cargar]);

  const liquidar = async () => {
    if (!window.confirm(
      `Liquidar ${MESES[mes-1]} de ${anio} por ${fmt(datos.total_a_pagar)}.\n\n` +
      `Las cifras quedan congeladas: si después se anula o edita una factura de este mes, ` +
      `lo pagado no cambia, pero el sistema te avisará de la diferencia.\n\n¿Continuar?`)) return;
    setOcupado(true);
    try {
      const { data } = await api.post('/api/vendedores/liquidar', { anio, mes });
      setMsg(data.mensaje); cargar();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setOcupado(false); }
  };

  const reabrir = async () => {
    if (!window.confirm(
      `Reabrir ${MESES[mes-1]} de ${anio}.\n\n` +
      `Se borra el registro de lo liquidado para poder corregirlo y volver a liquidar. ` +
      `Lo que ya se le haya pagado a la gente no se deshace solo.\n\n¿Continuar?`)) return;
    setOcupado(true);
    try {
      const { data } = await api.post('/api/vendedores/reabrir', { anio, mes });
      setMsg(data.mensaje); cargar();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setOcupado(false); }
  };

  if (!datos) return <div style={s.vacio}>{msg || 'Cargando…'}</div>;

  const conMovimiento = datos.detalle.filter(v => v.ventas > 0 || v.total_a_pagar > 0 || v.liquidado);

  return (
    <div>
      {msg && <div style={s.msg} onClick={() => setMsg('')}>{msg}</div>}

      <div style={s.barra}>
        <select value={mes} onChange={e => setMes(Number(e.target.value))} style={s.select}>
          {MESES.map((m, i) => <option key={m} value={i+1}>{m}</option>)}
        </select>
        <select value={anio} onChange={e => setAnio(Number(e.target.value))} style={s.select}>
          {[0,1,2].map(d => ahora.getFullYear() - d).map(a =>
            <option key={a} value={a}>{a}</option>)}
        </select>
        <span style={{ flex: 1 }} />
        {esAdmin && !datos.liquidado && datos.total_a_pagar > 0 && (
          <button onClick={liquidar} style={s.btnP} disabled={ocupado}>
            {ocupado ? 'Liquidando…' : `✓ Liquidar ${MESES[mes-1]}`}
          </button>
        )}
        {esAdmin && datos.liquidado && (
          <button onClick={reabrir} style={s.btn} disabled={ocupado}>Reabrir el mes</button>
        )}
      </div>

      <div style={s.numeros}>
        <Num n={fmt(datos.total_a_pagar)} l="a pagar este mes" color="#86EFAC" />
        <Num n={fmt(datos.bolsa_equipo)} l={`bolsa del equipo · ${fmt(datos.equipo_por_vendedor)} c/u`} color="#A5B4FC" />
        <Num n={datos.vendedores_activos} l="vendedores activos" color="#7DD3FC" />
        {datos.liquidado && <Num n={fmt(datos.total_pagado)} l="ya liquidado" color="#FDE047" />}
      </div>

      {datos.liquidado && datos.descuadrados > 0 && (
        <div style={s.aviso}>
          <b>Ojo: {datos.descuadrados} vendedor(es) quedaron descuadrados.</b> Este mes ya se
          liquidó, pero después se anuló o editó alguna factura, así que la cuenta de hoy ya
          no coincide con lo que se pagó. Lo pagado no se toca —la plata ya salió—; abajo se
          ve la diferencia de cada uno para que decidas si se compensa el mes siguiente o se
          reabre y se corrige.
        </div>
      )}

      {datos.liquidado && datos.descuadrados === 0 && (
        <div style={{ ...s.aviso, background:'rgba(34,197,94,0.07)',
          borderColor:'rgba(34,197,94,0.25)', color:'#86EFAC' }}>
          <b>{MESES[mes-1]} está liquidado y cuadrado.</b> Lo pagado coincide con lo calculado.
        </div>
      )}

      <div style={s.tabla}>
        <div style={{ ...s.fila, ...s.encabezado }}>
          <div style={{ flex:'2 1 160px' }}>Vendedor</div>
          <div style={{ width:'70px' }}>Ventas</div>
          <div style={{ width:'110px' }}>Vendió</div>
          <div style={{ width:'100px' }}>Suyo (5%)</div>
          <div style={{ width:'100px' }}>Equipo</div>
          <div style={{ width:'110px' }}>Total</div>
          {datos.liquidado && <div style={{ width:'120px' }}>Se le pagó</div>}
        </div>
        {conMovimiento.map(v => (
          <div key={v.id} style={s.fila}>
            <div style={{ flex:'2 1 160px', minWidth:0 }}>
              <b style={{ color:'#fff', fontSize:'13px' }}>{v.nombre}</b>
              {!v.activo && <span style={s.et}>inactivo</span>}
            </div>
            <div style={{ width:'70px', color:'rgba(255,255,255,0.5)', fontSize:'12.5px' }}>{v.ventas}</div>
            <div style={{ width:'110px', color:'rgba(255,255,255,0.55)', fontSize:'12.5px' }}>{fmt(v.vendido)}</div>
            <div style={{ width:'100px', color:'#86EFAC', fontSize:'12.5px' }}>{fmt(v.comision_propia)}</div>
            <div style={{ width:'100px', color:'#A5B4FC', fontSize:'12.5px' }}>{fmt(v.parte_equipo)}</div>
            <div style={{ width:'110px' }}>
              <b style={{ color:'#fff', fontSize:'13px' }}>{fmt(v.total_a_pagar)}</b>
            </div>
            {datos.liquidado && (
              <div style={{ width:'120px', fontSize:'12.5px' }}>
                {v.liquidado ? (
                  <>
                    <span style={{ color:'rgba(255,255,255,0.75)' }}>{fmt(v.pagado)}</span>
                    {v.diferencia !== 0 && (
                      <div style={{ fontSize:'11px', color: v.diferencia < 0 ? '#FCA5A5' : '#FDE047' }}>
                        {v.diferencia < 0 ? 'se pagó de más ' : 'falta '}
                        {fmt(Math.abs(v.diferencia))}
                      </div>
                    )}
                  </>
                ) : <span style={{ color:'rgba(255,255,255,0.3)' }}>sin liquidar</span>}
              </div>
            )}
          </div>
        ))}
        {!conMovimiento.length && (
          <div style={s.vacio}>Nadie vendió nada en {MESES[mes-1]} de {anio}.</div>
        )}
      </div>

      {historial.length > 0 && (
        <>
          <div style={s.tituloSec}>Meses ya liquidados</div>
          <div style={s.tabla}>
            {historial.map(h => (
              <button key={`${h.anio}-${h.mes}`} style={{ ...s.fila, ...s.filaBtn }}
                onClick={() => { setAnio(h.anio); setMes(h.mes); }}>
                <span style={{ flex:'1 1 150px', textAlign:'left', color:'#fff', fontSize:'13px' }}>
                  {MESES[h.mes-1]} {h.anio}
                </span>
                <span style={{ width:'130px', textAlign:'left', color:'rgba(255,255,255,0.5)', fontSize:'12.5px' }}>
                  {h.vendedores} vendedor(es)
                </span>
                <span style={{ width:'120px', textAlign:'left', color:'#86EFAC', fontSize:'12.5px' }}>
                  {fmt(h.total)}
                </span>
                <span style={{ flex:'1 1 140px', textAlign:'left', color:'rgba(255,255,255,0.35)', fontSize:'11.5px' }}>
                  {new Date(h.liquidado_en).toLocaleDateString('es-CO')}
                  {h.liquidado_por_nombre ? ` · ${h.liquidado_por_nombre}` : ''}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const Num = ({ n, l, color }) => (
  <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)',
    borderRadius:'11px', padding:'12px 15px', flex:'1 1 150px' }}>
    <div style={{ fontSize:'18px', fontWeight:'700', color }}>{n}</div>
    <div style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.42)' }}>{l}</div>
  </div>
);

const s = {
  barra:   { display:'flex', gap:'8px', marginBottom:'13px', flexWrap:'wrap', alignItems:'center' },
  select:  { padding:'8px 12px', borderRadius:'9px', border:'1px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.04)', color:'#fff', fontSize:'13px' },
  numeros: { display:'flex', gap:'9px', flexWrap:'wrap', marginBottom:'13px' },
  aviso:   { background:'rgba(251,191,36,0.07)', border:'1px solid rgba(251,191,36,0.25)', borderRadius:'10px', padding:'12px 15px', marginBottom:'13px', fontSize:'12.5px', color:'#FDE047', lineHeight:'1.65' },
  tabla:   { background:'rgba(255,255,255,0.02)', borderRadius:'12px', border:'1px solid rgba(255,255,255,0.06)', overflow:'hidden', marginBottom:'6px' },
  fila:    { display:'flex', gap:'12px', padding:'10px 14px', borderBottom:'1px solid rgba(255,255,255,0.05)', alignItems:'center', flexWrap:'wrap' },
  filaBtn: { width:'100%', background:'none', border:'none', borderBottom:'1px solid rgba(255,255,255,0.05)', cursor:'pointer' },
  encabezado:{ fontSize:'11px', color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.5px', background:'rgba(255,255,255,0.02)' },
  et:      { fontSize:'10px', color:'rgba(255,255,255,0.4)', background:'rgba(255,255,255,0.06)', padding:'1px 6px', borderRadius:'8px', marginLeft:'7px' },
  tituloSec:{ fontSize:'11px', color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.5px', margin:'18px 0 7px' },
  vacio:   { padding:'26px', textAlign:'center', color:'rgba(255,255,255,0.3)', fontSize:'13px' },
  msg:     { background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.25)', color:'#86EFAC', padding:'10px 14px', borderRadius:'9px', marginBottom:'12px', fontSize:'13px', cursor:'pointer' },
  btn:     { padding:'8px 14px', borderRadius:'9px', fontSize:'12.5px', cursor:'pointer', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.6)' },
  btnP:    { padding:'8px 16px', borderRadius:'9px', fontSize:'12.5px', cursor:'pointer', background:'rgba(34,197,94,0.18)', border:'1px solid rgba(34,197,94,0.45)', color:'#86EFAC', fontWeight:'600' },
};
